import { useSession } from '../../app/session.js';
import { TopActions, useView, useViewData, useViewOrFallback, WithData } from '../../app/view.js';
import { Tile } from '../../components/Art.js';
import { Icon } from '../../components/Icon.js';
import { useModal } from '../../components/Modal.js';
import { EmptyCard, Pill } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { fmtDay, fmtDayShort, qs, rp } from '../../lib/format.js';
import { label } from '../../lib/status.js';
import { useOpenNota } from '../nota/open.js';
import { type StoreRow, useStoreForm } from './storeForm.js';

// Toko / Client — owner/admin: semua toko; sales: "Toko Saya" (port A.views.toko + store-open v1 di admin.js).

interface StoreNota {
  id: number;
  no: string;
  status: string;
  ship_date: string;
  delivered_date: string | null;
  total: number;
  pay_method: string;
}

/** Detail toko (modal) — v1 A.acts['store-open']. Bisa dipakai di luar halaman (pencarian global). */
export function useStoreDetail() {
  const { open } = useModal();
  const { me, can } = useSession();
  // Juga dibuka dari luar halaman (palet pencarian) — lihat useViewOrFallback().
  const { go, reload } = useViewOrFallback();
  const storeForm = useStoreForm();
  const openNota = useOpenNota();
  return async (id: number) => {
    const { item: s, notas } = await api<{ item: StoreRow; notas: StoreNota[] }>('GET', `/api/stores/${id}`);
    open({
      wide: true,
      title: s.name,
      body: (m) => (
        <>
          <dl className="kv">
            <dt>Kode</dt>
            <dd className="mono">{s.code}</dd>
            <dt>Pemilik</dt>
            <dd>{s.owner_name || '-'}</dd>
            <dt>Telepon</dt>
            <dd>{s.phone ? <a href={`tel:${s.phone.replace(/[^\d+]/g, '')}`}>{s.phone}</a> : '-'}</dd>
            <dt>Alamat</dt>
            <dd>
              {`${s.address || '-'} ${s.lat != null ? '· ' : ''}`}
              {s.lat != null ? (
                <a target="_blank" rel="noopener" href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`}>
                  peta
                </a>
              ) : null}
            </dd>
            <dt>Area</dt>
            <dd>{s.area || '-'}</dd>
            <dt>Sales</dt>
            <dd>{s.sales_name || '-'}</dd>
            <dt>Termin tempo</dt>
            <dd>{s.term_days ? `${s.term_days} hari` : 'Tidak ada'}</dd>
            <dt>Batas kredit</dt>
            <dd>{s.credit_limit ? rp(s.credit_limit) : 'Tanpa batas'}</dd>
            <dt>Piutang</dt>
            <dd className={s.piutang ? 'danger-txt' : ''}>{rp(s.piutang)}</dd>
          </dl>
          <b>Nota terakhir</b>
          <div className="list">
            {notas.length ? (
              notas.map((n) => (
                <div
                  key={n.id}
                  className="li click"
                  data-m="nota"
                  data-id={n.id}
                  onClick={() => {
                    m.close();
                    void openNota(n.id);
                  }}
                >
                  <div className="body">
                    <b className="mono">{n.no}</b>
                    <span>{`${fmtDay(n.delivered_date || n.ship_date)} · ${label('pm', n.pay_method)}`}</span>
                  </div>
                  <div className="end">
                    <b>{rp(n.total)}</b>
                    <Pill kind="nota" v={n.status} />
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">Belum ada transaksi.</div>
            )}
          </div>
        </>
      ),
      foot: (m) => (
        <>
          {me.role === 'sales' && can('spo.buat') ? (
            <button
              type="button"
              className="btn btn-soft"
              data-m="spo"
              onClick={() => {
                m.close();
                go(`/spo-baru?store=${s.id}`);
              }}
            >
              SPO untuk toko ini
            </button>
          ) : null}
          {can('toko.ubah') ? (
            <button
              type="button"
              className="btn btn-primary"
              data-m="edit"
              onClick={() => {
                m.close();
                storeForm(s as unknown as Record<string, unknown>, () => reload());
              }}
            >
              Ubah
            </button>
          ) : null}
        </>
      ),
    });
  };
}

function List({ items }: { items: StoreRow[] }) {
  const { r, go } = useView();
  const { isMgr } = useSession();
  const openStore = useStoreDetail();
  return (
    <>
      <form
        className="filters"
        id="tkF"
        onSubmit={(e) => {
          e.preventDefault();
          const f = e.currentTarget;
          const all = f.elements.namedItem('all') as HTMLInputElement | null;
          go(`/toko?${qs({ q: (f.elements.namedItem('q') as HTMLInputElement).value, all: all?.checked ? 1 : '' })}`);
        }}
      >
        <input
          autoComplete="off"
          className="inp search"
          name="q"
          placeholder="Cari toko, area, pemilik…"
          defaultValue={r.q.q || ''}
        />
        {isMgr ? (
          <label className="check">
            <input type="checkbox" name="all" defaultChecked={!!r.q.all} />
            {' termasuk nonaktif'}
          </label>
        ) : null}
        <button className="btn btn-sm">Cari</button>
      </form>
      <div className="feed">
        {items.length ? (
          items.map((s) => (
            <div key={s.id} className="li click fcard" data-act="store-open" data-id={s.id} onClick={() => void openStore(s.id)}>
              <Tile n="store" id={s.id} cls="tile-lg" />
              <div className="body">
                <b>
                  {`${s.name} `}
                  <span className="mono muted small">{s.code}</span>
                  {s.active ? null : (
                    <>
                      {' '}
                      <span className="pill t-slate">nonaktif</span>
                    </>
                  )}
                </b>
                <span>
                  {`${[s.owner_name, s.area, s.sales_name].filter(Boolean).join(' · ')}${s.term_days ? ` · tempo ${s.term_days} hr` : ''}${s.lat == null ? ' · ' : ''}`}
                  {s.lat == null ? <span className="warn-txt">tanpa titik GPS</span> : null}
                </span>
              </div>
              <div className="end">
                {s.piutang ? (
                  <>
                    <b className="danger-txt">{rp(s.piutang)}</b>
                    <span className="small muted">piutang</span>
                  </>
                ) : null}
                {s.last_order ? <span className="small muted">{`order ${fmtDayShort(s.last_order)}`}</span> : null}
              </div>
            </div>
          ))
        ) : (
          <EmptyCard>Belum ada toko.</EmptyCard>
        )}
      </div>
    </>
  );
}

export default function Toko() {
  const { r, reload } = useView();
  const { can } = useSession();
  const storeForm = useStoreForm();
  const q = useViewData<{ items: StoreRow[] }>(
    ['toko', r.q.q || '', r.q.all || ''],
    `/api/stores?${qs({ q: r.q.q || '', active: r.q.all ? 'all' : '' })}`,
  );
  return (
    <>
      <TopActions>
        {can('toko.tambah') ? (
          <button className="btn btn-primary btn-sm" data-act="store-new" onClick={() => storeForm(null, () => reload())}>
            <Icon n="plus" cls="i-sm" /> Toko
          </button>
        ) : null}
      </TopActions>
      <WithData q={q}>{(d) => <List items={d.items} />}</WithData>
    </>
  );
}
